import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import { formatDisplayDate } from '../../utils/dateUtils';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import DeliverableListView from './components/DeliverableListView';
import GanttChartView from './components/GanttChartView';
import ResourceGanttView from './components/ResourceGanttView';
import AddDeliverableModal from './components/AddDeliverableModal';
import EditPhaseModal from './components/EditPhaseModal';
import ProjectDetailSpreadsheetView from './components/ProjectDetailSpreadsheetView';
import ProjectTimeline from './components/ProjectTimeline';
import SectionDetailModal from './components/SectionDetailModal';

import ProjectStatusBadge from '../project-management/components/ProjectStatusBadge';
import ProjectProgressBar from '../project-management/components/ProjectProgressBar';

import { projectService } from '../../services/projectService';
import { resourceService } from '../../services/resourceService';
import { supabase } from '../../lib/supabase';
import { createRealtimeSubscription } from '../../utils/realtimeErrorHandler';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import AddPhasesModal from './components/AddPhasesModal';
import { projectDocumentService } from '../../services/projectDocumentService';
import { projectNoteService } from '../../services/projectNoteService';
import { projectImageService } from '../../services/projectImageService';
import { projectChangeOrderService } from '../../services/projectChangeOrderService';
import { getProjectContractors } from '../../services/contractorVendorLinkService';
import ExportModal from './components/ExportModal';
import AdjustDatesModal from './components/AdjustDatesModal';
import { deliverableService } from '../../services/deliverableService';
import PresenceBadges from '../../components/PresenceBadges';
import { joinPresenceChannel } from '../../services/presenceService';


const ProjectDetailManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'administrator' || userProfile?.role === 'global_administrator';
  const [project, setProject] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [resources, setResources] = useState([]);
  const [viewMode, setViewMode] = useState('detail');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingDeliverable, setEditingDeliverable] = useState(null);
  const [defaultRowType, setDefaultRowType] = useState('deliverable');
  const [defaultParentId, setDefaultParentId] = useState('');
  const [defaultPhaseId, setDefaultPhaseId] = useState('');
  const [editPhaseModal, setEditPhaseModal] = useState(null); // { id, name, startDate, endDate, status }
  const [editPhaseForm, setEditPhaseForm] = useState({});
  const [editPhaseErrors, setEditPhaseErrors] = useState({});
  const [savingEditPhase, setSavingEditPhase] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState('overview');
  const [customers, setCustomers] = useState([]);
  const [pmUsers, setPmUsers] = useState([]);
  const [phases, setPhases] = useState([]);
  const [inlineDeliverable, setInlineDeliverable] = useState(null);
  const [showInlineAdd, setShowInlineAdd] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [completeChildrenDialog, setCompleteChildrenDialog] = useState(null); // { type: 'phase'|'deliverable'|'task', id, projectId, name }
  const [editingPhaseName, setEditingPhaseName] = useState(null); // { id, value }
  const [savingPhaseName, setSavingPhaseName] = useState(null); // phaseId being saved
  const [infoSectionCollapsed, setInfoSectionCollapsed] = useState(false);
  const [editingPhaseDate, setEditingPhaseDate] = useState(null); // { id, field: 'startDate'|'endDate', value }
  const [savingPhaseDate, setSavingPhaseDate] = useState(null); // phaseId being saved
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [phaseModalOpen, setPhaseModalOpen] = useState(false);
  const [phaseModalMode, setPhaseModalMode] = useState('add'); // 'add' | 'edit'
  const [phaseModalTarget, setPhaseModalTarget] = useState(null);
  const [phaseActionConfirm, setPhaseActionConfirm] = useState(null); // { action: 'edit'|'delete', phase, activeCount }
  const [addPhasesModalOpen, setAddPhasesModalOpen] = useState(false);
  const [contentSectionCollapsed, setContentSectionCollapsed] = useState(false);

  // ── Export Modal state ────────────────────────────────────────────────────
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // ── Adjust Dates Modal state ──────────────────────────────────────────────
  const [adjustDatesModalOpen, setAdjustDatesModalOpen] = useState(false);
  const [adjustDatesSaving, setAdjustDatesSaving] = useState(false);

  // ── Presence state ────────────────────────────────────────────────────────
  const [presenceList, setPresenceList] = useState([]);
  const presenceLeaveRef = useRef(null);

  // ── Contractors & Vendors state ──────────────────────────────────────────
  const [projectContractors, setProjectContractors] = useState({ generalContractors: [], subContractors: [], vendors: [] });
  const [contractorsLoading, setContractorsLoading] = useState(false);

  // ── Notes state ────────────────────────────────────────────────────────────
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  // Add Note modal
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  // Edit Note modal
  const [editingNote, setEditingNote] = useState(null); // full note object
  const [editNoteTitle, setEditNoteTitle] = useState('');
  const [editNoteText, setEditNoteText] = useState('');

  // ── Project Documents state ──────────────────────────────────────────────
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const fileInputRef = useRef(null);
  // Add Document modal
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocNote, setNewDocNote] = useState('');
  const [newDocSource, setNewDocSource] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);
  // Edit Document modal
  const [editingDoc, setEditingDoc] = useState(null);
  const [editDocTitle, setEditDocTitle] = useState('');
  const [editDocNote, setEditDocNote] = useState('');
  const [editDocSource, setEditDocSource] = useState('');

  // ── Project Images state ─────────────────────────────────────────────────
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  // Add Image modal
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [newImageTitle, setNewImageTitle] = useState('');
  const [newImageNote, setNewImageNote] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImageInputMode, setNewImageInputMode] = useState('url'); // 'url' | 'upload' | 'paste' | 'camera'
  const [newImagePasteData, setNewImagePasteData] = useState('');
  const [newImagePasteFile, setNewImagePasteFile] = useState(null);
  const [newImagePastePreview, setNewImagePastePreview] = useState('');
  const [savingImg, setSavingImg] = useState(false);
  // Edit Image modal
  const [editingImage, setEditingImage] = useState(null);
  const [editImageTitle, setEditImageTitle] = useState('');
  const [editImageNote, setEditImageNote] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editImageSignedUrl, setEditImageSignedUrl] = useState('');
  const [deletingImageId, setDeletingImageId] = useState(null);
  const addImageFileRef = useRef(null);
  const addImageCameraRef = useRef(null);

  // ── Project Change Orders state ──────────────────────────────────────────
  const [changeOrders, setChangeOrders] = useState([]);
  const [changeOrdersLoading, setChangeOrdersLoading] = useState(false);
  const [savingChangeOrder, setSavingChangeOrder] = useState(false);
  const [deletingChangeOrderId, setDeletingChangeOrderId] = useState(null);
  // Add Change Order modal
  const [showAddChangeOrderModal, setShowAddChangeOrderModal] = useState(false);
  const [newCoTitle, setNewCoTitle] = useState('');
  const [newCoDescription, setNewCoDescription] = useState('');
  const [newCoImpact, setNewCoImpact] = useState('');
  // Edit Change Order modal
  const [editingChangeOrder, setEditingChangeOrder] = useState(null);
  const [editCoTitle, setEditCoTitle] = useState('');
  const [editCoDescription, setEditCoDescription] = useState('');
  const [editCoImpact, setEditCoImpact] = useState('');

  // ── Section Detail Modal state ────────────────────────────────────────────
  const [sectionDetailOpen, setSectionDetailOpen] = useState(false);
  const [sectionDetailType, setSectionDetailType] = useState(null); // 'notes' | 'documents' | 'images' | 'changeOrders'

  const openSectionDetail = (type) => {
    setSectionDetailType(type);
    setSectionDetailOpen(true);
  };

  const getSectionDetailData = () => {
    switch (sectionDetailType) {
      case 'notes':return notes;
      case 'documents':return documents;
      case 'images':return images;
      case 'changeOrders':return changeOrders;
      default:return [];
    }
  };

  // Resizable boxes state — widths stored as percentages [overview%, phases%, team%, contractors%]
  const BOX_WIDTHS_KEY = 'projectDetailBoxWidths4';
  const DEFAULT_WIDTHS = [19, 19, 34, 24];
  const [boxWidths, setBoxWidths] = useState(() => {
    try {
      const stored = localStorage.getItem(BOX_WIDTHS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed?.length === 4) return parsed;
      }
    } catch {}
    return DEFAULT_WIDTHS;
  });
  const isDragging = useRef(null); // null | 0 | 1 | 2
  const dragStartX = useRef(0);
  const dragStartWidths = useRef([...DEFAULT_WIDTHS]);
  const containerRef = useRef(null);

  const handleDividerMouseDown = useCallback((dividerIndex, e) => {
    e?.preventDefault();
    isDragging.current = dividerIndex;
    dragStartX.current = e?.clientX;
    dragStartWidths.current = [...boxWidths];
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [boxWidths]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging?.current === null || !containerRef?.current) return;
      const containerWidth = containerRef?.current?.getBoundingClientRect()?.width;
      const deltaPercent = (e?.clientX - dragStartX?.current) / containerWidth * 100;
      const MIN_WIDTH = 10;
      const newWidths = [...dragStartWidths?.current];

      if (isDragging?.current === 0) {
        // Dragging divider between box 0 and box 1
        let w0 = dragStartWidths?.current?.[0] + deltaPercent;
        let w1 = dragStartWidths?.current?.[1] - deltaPercent;
        if (w0 < MIN_WIDTH) {w1 += w0 - MIN_WIDTH;w0 = MIN_WIDTH;}
        if (w1 < MIN_WIDTH) {w0 += w1 - MIN_WIDTH;w1 = MIN_WIDTH;}
        newWidths[0] = w0;
        newWidths[1] = w1;
      } else if (isDragging?.current === 1) {
        // Dragging divider between box 1 and box 2
        let w1 = dragStartWidths?.current?.[1] + deltaPercent;
        let w2 = dragStartWidths?.current?.[2] - deltaPercent;
        if (w1 < MIN_WIDTH) {w2 += w1 - MIN_WIDTH;w1 = MIN_WIDTH;}
        if (w2 < MIN_WIDTH) {w1 += w2 - MIN_WIDTH;w2 = MIN_WIDTH;}
        newWidths[1] = w1;
        newWidths[2] = w2;
      } else {
        // Dragging divider between box 2 and box 3
        let w2 = dragStartWidths?.current?.[2] + deltaPercent;
        let w3 = dragStartWidths?.current?.[3] - deltaPercent;
        if (w2 < MIN_WIDTH) {w3 += w2 - MIN_WIDTH;w2 = MIN_WIDTH;}
        if (w3 < MIN_WIDTH) {w2 += w3 - MIN_WIDTH;w3 = MIN_WIDTH;}
        newWidths[2] = w2;
        newWidths[3] = w3;
      }
      setBoxWidths(newWidths);
    };

    const handleMouseUp = () => {
      if (isDragging?.current === null) return;
      isDragging.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist to localStorage
      setBoxWidths((prev) => {
        try {localStorage.setItem(BOX_WIDTHS_KEY, JSON.stringify(prev));} catch {}
        return prev;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Get project ID from URL params or location state
  const projectId = id || location?.state?.projectId;

  useEffect(() => {
    if (!projectId) {
      navigate('/project-management');
      return;
    }

    loadProjectData();
    loadCustomers();
    loadPMUsers();
    loadDeliverables();
    loadResources();
    loadPhases();
    loadProjectAssignments();
    loadAllAssignments();
    loadNotes();
    loadDocuments();
    loadImages();
    loadChangeOrders();
    loadProjectContractors();

    // Error handler for subscription
    const handleError = (error) => {
      // Real-time is non-critical; log quietly without surfacing UI errors
      if (!error?.willRetry) {
        console.warn('Deliverables real-time subscription unavailable; falling back to manual refresh.');
      }
    };

    // Real-time subscription for deliverables
    const { cleanup: cleanupDeliverables } = createRealtimeSubscription({
      supabase,
      channelName: 'deliverables_changes',
      tableName: 'deliverables',
      filter: `project_id=eq.${projectId}`,
      onDataChange: () => {
        loadDeliverables(true);
      },
      onError: handleError
    });

    // Real-time subscription for phases
    const { cleanup: cleanupPhases } = createRealtimeSubscription({
      supabase,
      channelName: `phases_changes_${projectId}`,
      tableName: 'project_phases',
      filter: `project_id=eq.${projectId}`,
      onDataChange: () => {
        loadPhases();
      },
      onError: (err) => {
        // Phases real-time is non-critical; log quietly without surfacing UI errors
        if (!err?.willRetry) {
          console.warn('Phases real-time subscription unavailable; falling back to manual refresh.');
        }
      }
    });

    return () => {
      cleanupDeliverables();
      cleanupPhases();
    };
  }, [projectId]);

  // ── Presence: join channel for this project ───────────────────────────────
  useEffect(() => {
    if (!projectId || !userProfile) return;

    const { leave } = joinPresenceChannel(
      `project:${projectId}`,
      userProfile,
      (list) => setPresenceList(list)
    );
    presenceLeaveRef.current = leave;

    return () => {
      presenceLeaveRef?.current?.();
      presenceLeaveRef.current = null;
    };
  }, [projectId, userProfile]);

  const loadProjectData = async () => {
    try {
      const { data, error: fetchError } = await projectService?.getById(projectId);
      if (fetchError) throw fetchError instanceof Error ? fetchError : new Error(fetchError?.message || 'Failed to load project');
      setProject(data);
    } catch (err) {
      console.error('Error loading project:', err?.message);
      setError(err?.message || 'Failed to load project');
    }
  };

  const loadCustomers = async () => {
    try {
      const { data, error } = await supabase?.from('customers')?.select('id, company_name')?.order('company_name', { ascending: true });
      if (error) throw error instanceof Error ? error : new Error(error?.message || 'Failed to load customers');
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err?.message);
      setCustomers([]);
    }
  };

  const loadPMUsers = async () => {
    try {
      const { data, error } = await supabase?.
      from('user_profiles')?.
      select('id, full_name, first_name, last_name, nickname, email')?.
      eq('is_pm', true)?.
      eq('is_active', true)?.
      order('full_name', { ascending: true });
      if (error) throw error instanceof Error ? error : new Error(error?.message || 'Failed to load PM users');
      setPmUsers(data || []);
    } catch (err) {
      console.error('Error fetching PM users:', err?.message);
      setPmUsers([]);
    }
  };

  const loadDeliverables = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await projectService?.getDeliverables(projectId);
      if (fetchError) throw fetchError instanceof Error ? fetchError : new Error(fetchError?.message || 'Failed to load deliverables');
      const loaded = data || [];
      // Auto-assign order numbers based on start_date for items that don't have one yet
      const needsInit = loaded?.some((i) => i?.customOrder == null || i?.customOrder === '');
      if (needsInit) {
        const { updates } = await deliverableService?.initOrdersFromStartDate(loaded);
        if (updates?.length) {
          // Apply the computed orders to the local state immediately
          const orderById = {};
          updates?.forEach((u) => {orderById[u?.id] = u?.customOrder;});
          const updated = loaded?.map((i) => orderById?.[i?.id] != null ? { ...i, customOrder: orderById?.[i?.id] } : i);
          setDeliverables(updated);
          if (!silent) setLoading(false);
          return;
        }
      }
      setDeliverables(loaded);
    } catch (err) {
      console.error('Error loading deliverables:', err?.message);
      setError(err?.message || 'Failed to load deliverables');
      setDeliverables([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadResources = async () => {
    try {
      const { data } = await resourceService?.getAll();
      setResources(data || []);
    } catch (err) {}
  };

  const loadProjectContractors = async () => {
    if (!projectId) return;
    setContractorsLoading(true);
    try {
      const data = await getProjectContractors(projectId);
      setProjectContractors(data || { generalContractors: [], subContractors: [], vendors: [] });
    } catch (err) {
      console.error('Error loading project contractors:', err?.message);
    } finally {
      setContractorsLoading(false);
    }
  };

  const loadProjectAssignments = async () => {
    try {
      // Query deliverable_resource_assignments joined through deliverables for this project
      const { data, error } = await supabase?.
      from('deliverable_resource_assignments')?.
      select(`
          id,
          start_date,
          end_date,
          assignment_start_date,
          assignment_end_date,
          resource:resources!deliverable_resource_assignments_resource_id_fkey(id, name, category, status),
          deliverable:deliverables!inner(id, project_id)
        `)?.
      eq('deliverable.project_id', projectId);

      if (error) throw error;

      // Map to camelCase shape expected by the rendering code
      const mapped = (data || [])?.map((row) => ({
        id: row?.id,
        startDate: row?.start_date || row?.assignment_start_date,
        endDate: row?.end_date || row?.assignment_end_date,
        resource: row?.resource,
        resource_id: row?.resource?.id
      }));

      setProjectAssignments(mapped);
    } catch (err) {
      console.error('Error loading project assignments:', err?.message);
      setProjectAssignments([]);
    }
  };

  // Load ALL assignments across all projects for cross-project conflict detection
  const loadAllAssignments = async () => {
    try {
      const { data, error } = await supabase?.
      from('deliverable_resource_assignments')?.
      select(`
          id,
          start_date,
          end_date,
          assignment_start_date,
          assignment_end_date,
          resource_id,
          deliverable:deliverables!inner(id, project_id)
        `);

      if (error) throw error;

      const mapped = (data || [])?.map((row) => ({
        id: row?.id,
        startDate: row?.start_date || row?.assignment_start_date,
        endDate: row?.end_date || row?.assignment_end_date,
        resource_id: row?.resource_id,
        project_id: row?.deliverable?.project_id
      }));

      setAllAssignments(mapped);
    } catch (err) {
      console.error('Error loading all assignments:', err?.message);
      setAllAssignments([]);
    }
  };

  const loadPhases = async () => {
    try {
      const { data } = await projectService?.getPhases(projectId);
      setPhases(data || []);
    } catch (err) {
      console.error('Error loading phases:', err?.message);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleStartEdit = () => {
    if (!project) return;
    setEditForm({
      projectName: project?.projectName || '',
      customerName: project?.customerName || '',
      totalBudget: project?.totalBudget?.toString() || '',
      startDate: project?.startDate || '',
      endDate: project?.endDate || '',
      location: project?.location || '',
      description: project?.description || '',
      status: project?.status || 'Planning',
      assignedPmId: project?.assignedPmId || project?.assigned_pm_id || ''
    });
    setEditErrors({});
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
    setEditErrors({});
  };

  const validateEditForm = () => {
    const errs = {};
    if (!editForm?.projectName?.trim()) errs.projectName = 'Project name is required';
    if (!editForm?.customerName) errs.customerName = 'Customer is required';
    if (!editForm?.startDate) errs.startDate = 'Start date is required';
    if (!editForm?.endDate) errs.endDate = 'End date is required';
    if (editForm?.startDate && editForm?.endDate && new Date(editForm.startDate) >= new Date(editForm.endDate))
    errs.endDate = 'End date must be after start date';
    if (!editForm?.totalBudget || parseFloat(editForm?.totalBudget) <= 0)
    errs.totalBudget = 'Valid budget is required';
    if (!editForm?.location?.trim()) errs.location = 'Location is required';
    setEditErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSaveEdit = async () => {
    if (!validateEditForm()) return;
    setIsSaving(true);
    const prevPmId = project?.assignedPmId || project?.assigned_pm_id;
    try {
      const projectData = {
        projectName: editForm?.projectName,
        customerName: editForm?.customerName,
        status: editForm?.status,
        totalBudget: parseFloat(editForm?.totalBudget),
        startDate: editForm?.startDate,
        endDate: editForm?.endDate,
        location: editForm?.location,
        description: editForm?.description || '',
        assignedPmId: editForm?.assignedPmId || null,
        convertedFromBid: project?.convertedFromBid || false,
        bidNumber: project?.bidNumber || null,
        conversionDate: project?.conversionDate || null,
        costBreakdown: project?.costBreakdown || []
      };
      const { error: updateError } = await projectService?.update(projectId, projectData);
      if (updateError) throw updateError instanceof Error ? updateError : new Error(updateError?.message || 'Failed to update project');

      // If status set to Cancelled, archive the project and navigate to project management
      if (editForm?.status === 'Cancelled') {
        await projectService?.cancel(projectId, project?.status);
        showNotification('Project cancelled and moved to archive');
        navigate('/project-management');
        return;
      }

      // Notify new PM if changed
      const newPmId = editForm?.assignedPmId;
      if (newPmId && newPmId !== prevPmId) {
        const updatedProject = { ...project, ...projectData, id: projectId };
        await notificationService?.createPMAssignmentNotification(newPmId, updatedProject);
      }

      await loadProjectData();
      setIsEditing(false);
      setEditForm({});
      showNotification('Project updated successfully');
    } catch (err) {
      showNotification(err?.message || 'Failed to update project', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    setIsDeleting(true);
    try {
      const { error: deleteError } = await projectService?.delete(projectId);
      if (deleteError) throw deleteError instanceof Error ? deleteError : new Error(deleteError?.message || 'Failed to delete project');
      navigate('/project-management');
    } catch (err) {
      showNotification(err?.message || 'Failed to delete project', 'error');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddDeliverable = async (deliverableData) => {
    try {
      const { error: createError } = await projectService?.createDeliverable({ ...deliverableData, projectId });
      if (createError) throw createError instanceof Error ? createError : new Error(createError?.message || 'Failed to create deliverable');
      // After creation, reload and then auto-assign order for the new item based on start date
      const { data: freshData } = await projectService?.getDeliverables(projectId);
      const freshItems = freshData || [];
      const needsInit = freshItems?.some((i) => i?.customOrder == null || i?.customOrder === '');
      if (needsInit) {
        const { updates } = await deliverableService?.initOrdersFromStartDate(freshItems);
        if (updates?.length) {
          const orderById = {};
          updates?.forEach((u) => {orderById[u?.id] = u?.customOrder;});
          const updated = freshItems?.map((i) => orderById?.[i?.id] != null ? { ...i, customOrder: orderById?.[i?.id] } : i);
          setDeliverables(updated);
          setIsAddModalOpen(false);
          const typeLabel = deliverableData?.rowType === 'task' ? 'Task' : deliverableData?.rowType === 'subtask' ? 'Sub-task' : 'Deliverable';
          showNotification(`${typeLabel} created successfully`);
          return;
        }
      }
      setDeliverables(freshItems);
      setIsAddModalOpen(false);
      const typeLabel = deliverableData?.rowType === 'task' ? 'Task' : deliverableData?.rowType === 'subtask' ? 'Sub-task' : 'Deliverable';
      showNotification(`${typeLabel} created successfully`);
    } catch (err) {
      throw err instanceof Error ? err : new Error(err?.message || 'Failed to create deliverable');
    }
  };

  const handleUpdateDeliverable = async (id, deliverableData) => {
    try {
      const { error: updateError } = await projectService?.updateDeliverable(id, deliverableData);
      if (updateError) throw updateError instanceof Error ? updateError : new Error(updateError?.message || 'Failed to update deliverable');
      await loadDeliverables(true);
      // Refresh editingDeliverable with the saved data so the modal stays populated
      const { data: freshData } = await projectService?.getDeliverables(projectId);
      if (freshData) {
        const updated = freshData?.find((d) => d?.id === id);
        if (updated) setEditingDeliverable(updated);
      }
      showNotification('Deliverable updated successfully');

      // If status changed to completed, prompt about children
      if (deliverableData?.status === 'completed') {
        const item = deliverables?.find((d) => d?.id === id);
        const rowType = item?.rowType || 'deliverable';
        if (rowType === 'phase') {
          // Check if it has deliverables
          const hasDeliverables = deliverables?.some((d) => d?.rowType === 'deliverable' && d?.phaseId === id);
          if (hasDeliverables) {
            setCompleteChildrenDialog({ type: 'phase', id, projectId, name: item?.name });
          }
        } else if (rowType === 'deliverable') {
          // Check if it has tasks
          const hasTasks = deliverables?.some((d) => d?.rowType === 'task' && d?.parentId === id);
          if (hasTasks) {
            setCompleteChildrenDialog({ type: 'deliverable', id, projectId, name: item?.name });
          }
        } else if (rowType === 'task') {
          // Check if it has subtasks
          const hasSubtasks = deliverables?.some((d) => d?.rowType === 'subtask' && d?.parentId === id);
          if (hasSubtasks) {
            setCompleteChildrenDialog({ type: 'task', id, projectId, name: item?.name });
          }
        }
      }
    } catch (err) {
      throw err instanceof Error ? err : new Error(err?.message || 'Failed to update deliverable');
    }
  };

  // Handle bulk reorder of deliverables/tasks/subtasks
  const handleReorder = async (updates) => {
    // updates: [{ id, customOrder }]
    try {
      const promises = updates?.map(({ id, customOrder }) =>
      projectService?.updateDeliverable(id, { customOrder })
      );
      await Promise.all(promises);
      await loadDeliverables(true);
    } catch (err) {
      showNotification(err?.message || 'Failed to reorder items', 'error');
    }
  };

  // Handle adjust project dates (shift all deliverable/task/subtask dates)
  const handleAdjustDates = async ({ mode, days, date, earliestStart, latestEnd }) => {
    setAdjustDatesSaving(true);
    try {
      if (mode === 'sync') {
        // Update only the project's start_date and end_date fields
        const updateData = {};
        if (earliestStart) updateData.startDate = earliestStart;
        if (latestEnd) updateData.endDate = latestEnd;
        if (Object.keys(updateData)?.length === 0) {
          showNotification('No dated items found to sync from.', 'info');
          setAdjustDatesModalOpen(false);
          return;
        }
        const { error: updateError } = await projectService?.update(projectId, updateData);
        if (updateError) throw updateError instanceof Error ? updateError : new Error(updateError?.message || 'Failed to sync project dates');
        await loadProjectData();
        setAdjustDatesModalOpen(false);
        showNotification('Project start and end dates updated to match task dates.');
      } else {
        const payload = mode === 'specific' ? { specificDate: date } : { days };
        const { error, count, shiftDays } = await deliverableService?.bulkShiftDates(projectId, payload);
        if (error) throw error;
        await loadDeliverables(true);
        setAdjustDatesModalOpen(false);
        if (count === 0) {
          showNotification('No dated items found to adjust.', 'info');
        } else {
          const direction = shiftDays > 0 ? 'later' : 'earlier';
          const absDays = Math.abs(shiftDays);
          showNotification(
            mode === 'specific'
              ? `${count} item${count !== 1 ? 's' : ''} shifted to new start date.`
              : `${count} item${count !== 1 ? 's' : ''} moved ${absDays} day${absDays !== 1 ? 's' : ''} ${direction}.`
          );
        }
      }
    } catch (err) {
      showNotification(err?.message || 'Failed to adjust dates', 'error');
    } finally {
      setAdjustDatesSaving(false);
    }
  };

  const handlePhaseStatusChange = async (phaseId, newStatus, phaseName) => {
    try {
      const { error } = await projectService?.updatePhase(phaseId, { status: newStatus, projectId });
      if (error) throw error;
      await loadPhases();
      showNotification('Phase status updated');
      if (newStatus === 'Completed') {
        // Check if phase has any deliverables
        const hasChildren = deliverables?.some((d) => d?.phaseId === phaseId);
        if (hasChildren) {
          setCompleteChildrenDialog({ type: 'phase', id: phaseId, projectId, name: phaseName });
        }
      }
    } catch (err) {
      showNotification(err?.message || 'Failed to update phase status', 'error');
    }
  };

  const handleCompleteChildrenConfirm = async () => {
    if (!completeChildrenDialog) return;
    const { type, id, projectId: pid } = completeChildrenDialog;
    try {
      if (type === 'phase') {
        await projectService?.bulkCompletePhaseChildren(pid, id);
      } else if (type === 'deliverable') {
        await projectService?.bulkCompleteDeliverableChildren(pid, id);
      } else if (type === 'task') {
        await projectService?.bulkCompleteTaskChildren(pid, id);
      }
      await loadDeliverables(true);
      showNotification('All children marked as Completed');
    } catch (err) {
      showNotification(err?.message || 'Failed to complete children', 'error');
    } finally {
      setCompleteChildrenDialog(null);
    }
  };

  const handleDeleteDeliverable = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deliverable?')) return;
    try {
      const { error: deleteError } = await projectService?.deleteDeliverable(id);
      if (deleteError) throw deleteError instanceof Error ? deleteError : new Error(deleteError?.message || 'Failed to delete deliverable');
      await loadDeliverables(true);
      showNotification('Deliverable deleted successfully');
    } catch (err) {
      showNotification(err?.message || 'Failed to delete deliverable', 'error');
    }
  };

  const handleDateChange = async (deliverableId, startDate, endDate) => {
    try {
      const { error: updateError } = await projectService?.updateDeliverable(deliverableId, { startDate, endDate });
      if (updateError) throw updateError instanceof Error ? updateError : new Error(updateError?.message || 'Failed to update dates');
      await loadDeliverables(true);
    } catch (err) {
      alert('Failed to update dates: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleAddChild = (parentItem, childType) => {
    setEditingDeliverable(null);
    setDefaultRowType(childType);
    setDefaultParentId(parentItem?.id);
    setDefaultPhaseId('');
    setIsAddModalOpen(true);
  };

  const handlePhaseNameDoubleClick = (phase) => {
    setEditingPhaseName({ id: phase?.id, value: phase?.name });
  };

  const handlePhaseNameSave = async (phaseId) => {
    if (!editingPhaseName || editingPhaseName?.id !== phaseId) return;
    const newName = editingPhaseName?.value?.trim();
    if (!newName) {
      setEditingPhaseName(null);
      return;
    }
    setSavingPhaseName(phaseId);
    try {
      const { error } = await projectService?.updatePhase(phaseId, { name: newName, projectId });
      if (error) throw error;
      await loadPhases();
      showNotification('Phase name updated');
    } catch (err) {
      showNotification(err?.message || 'Failed to update phase name', 'error');
    } finally {
      setSavingPhaseName(null);
      setEditingPhaseName(null);
    }
  };

  const handlePhaseNameKeyDown = (e, phaseId) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handlePhaseNameSave(phaseId);
    } else if (e?.key === 'Escape') {
      setEditingPhaseName(null);
    }
  };

  const handlePhaseDateDoubleClick = (phase, field) => {
    setEditingPhaseDate({ id: phase?.id, field, value: phase?.[field] || '' });
  };

  const handlePhaseDateSave = async (phaseId) => {
    if (!editingPhaseDate || editingPhaseDate?.id !== phaseId) return;
    const { field, value } = editingPhaseDate;
    setSavingPhaseDate(phaseId);
    try {
      const { error } = await projectService?.updatePhase(phaseId, { [field]: value || null, projectId });
      if (error) throw error;
      await loadPhases();
      showNotification('Phase date updated');
    } catch (err) {
      showNotification(err?.message || 'Failed to update phase date', 'error');
    } finally {
      setSavingPhaseDate(null);
      setEditingPhaseDate(null);
    }
  };

  const handlePhaseDateKeyDown = (e, phaseId) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handlePhaseDateSave(phaseId);
    } else if (e?.key === 'Escape') {
      setEditingPhaseDate(null);
    }
  };

  const handleOpenEditPhaseModal = (phase) => {
    setEditPhaseForm({
      name: phase?.name || '',
      startDate: phase?.startDate || '',
      endDate: phase?.endDate || '',
      status: phase?.status || 'Planning'
    });
    setEditPhaseErrors({});
    setEditPhaseModal(phase);
  };

  const handleSaveEditPhase = async () => {
    const errs = {};
    if (!editPhaseForm?.name?.trim()) errs.name = 'Phase name is required';
    if (!editPhaseForm?.startDate) errs.startDate = 'Start date is required';
    if (!editPhaseForm?.endDate) errs.endDate = 'End date is required';
    if (editPhaseForm?.startDate && editPhaseForm?.endDate && new Date(editPhaseForm.startDate) > new Date(editPhaseForm.endDate))
    errs.endDate = 'End date must be after start date';
    if (Object.keys(errs)?.length > 0) {setEditPhaseErrors(errs);return;}
    setSavingEditPhase(true);
    try {
      const { error } = await projectService?.updatePhase(editPhaseModal?.id, {
        name: editPhaseForm?.name?.trim(),
        startDate: editPhaseForm?.startDate,
        endDate: editPhaseForm?.endDate,
        status: editPhaseForm?.status,
        projectId
      });
      if (error) throw error;
      await loadPhases();
      await loadDeliverables(true);
      showNotification('Phase updated');
      setEditPhaseModal(null);
    } catch (err) {
      showNotification(err?.message || 'Failed to update phase', 'error');
    } finally {
      setSavingEditPhase(false);
    }
  };

  // ── New unified phase modal handlers ──────────────────────────────────────
  const handleOpenAddPhaseModal = () => {
    setPhaseModalMode('add');
    setPhaseModalTarget(null);
    setPhaseModalOpen(true);
  };

  const handleOpenEditPhaseFull = (phase) => {
    const activeDeliverables = deliverables?.filter(
      (d) => d?.phaseId === phase?.id && (!d?.rowType || d?.rowType === 'deliverable') &&
      d?.status !== 'completed'
    );
    if (activeDeliverables?.length > 0) {
      setPhaseActionConfirm({ action: 'edit', phase, activeCount: activeDeliverables?.length });
      return;
    }
    setPhaseModalMode('edit');
    setPhaseModalTarget(phase);
    setPhaseModalOpen(true);
  };

  const handlePhaseDeleteRequest = (phase) => {
    const activeDeliverables = deliverables?.filter(
      (d) => d?.phaseId === phase?.id && (!d?.rowType || d?.rowType === 'deliverable') &&
      d?.status !== 'completed'
    );
    if (activeDeliverables?.length > 0) {
      setPhaseActionConfirm({ action: 'delete', phase, activeCount: activeDeliverables?.length });
      return;
    }
    setPhaseModalMode('edit');
    setPhaseModalTarget(phase);
    setPhaseModalOpen(true);
    // Trigger delete confirm inside modal immediately
    setTimeout(() => {


      // The modal will open in edit mode; user can click Delete Phase inside
    }, 0); // Open modal in edit mode so user can use the Delete Phase button
    setPhaseModalMode('edit');setPhaseModalTarget(phase);
    setPhaseModalOpen(true);
  };

  const handlePhaseActionConfirmProceed = () => {
    if (!phaseActionConfirm) return;
    const { action, phase } = phaseActionConfirm;
    setPhaseActionConfirm(null);
    setPhaseModalMode('edit');
    setPhaseModalTarget(phase);
    setPhaseModalOpen(true);
  };

  const handlePhaseModalSave = async (formData, options) => {
    const { shiftFuturePhases, shiftChildren, dateShiftDays, originalPhase } = options || {};
    setSavingEditPhase(true);
    try {
      if (phaseModalMode === 'add') {
        // Create new phase
        const { error } = await projectService?.createPhase({ ...formData, projectId });
        if (error) throw error instanceof Error ? error : new Error(error?.message || 'Failed to create phase');
        await loadPhases();
        showNotification('Phase created successfully');
      } else {
        // Update existing phase
        const { error } = await projectService?.updatePhase(phaseModalTarget?.id, { ...formData, projectId });
        if (error) throw error instanceof Error ? error : new Error(error?.message || 'Failed to update phase');

        // Cascade: shift children (deliverables, tasks, subtasks) if date changed
        if (dateShiftDays !== 0 && shiftChildren) {
          const phaseDeliverables = deliverables?.filter(
            (d) => d?.phaseId === phaseModalTarget?.id && (!d?.rowType || d?.rowType === 'deliverable')
          );
          for (const del of phaseDeliverables) {
            const newDelStart = del?.startDate ?
            format(addDays(new Date(del.startDate), dateShiftDays), 'yyyy-MM-dd') :
            null;
            const newDelEnd = del?.endDate ?
            format(addDays(new Date(del.endDate), dateShiftDays), 'yyyy-MM-dd') :
            null;
            if (newDelStart || newDelEnd) {
              await projectService?.updateDeliverable(del?.id, {
                startDate: newDelStart || del?.startDate,
                endDate: newDelEnd || del?.endDate
              });
            }
            // Shift tasks under this deliverable
            const delTasks = deliverables?.filter((d) => d?.rowType === 'task' && d?.parentId === del?.id);
            for (const task of delTasks) {
              const newTaskStart = task?.startDate ?
              format(addDays(new Date(task.startDate), dateShiftDays), 'yyyy-MM-dd') :
              null;
              const newTaskEnd = task?.endDate ?
              format(addDays(new Date(task.endDate), dateShiftDays), 'yyyy-MM-dd') :
              null;
              if (newTaskStart || newTaskEnd) {
                await projectService?.updateDeliverable(task?.id, {
                  startDate: newTaskStart || task?.startDate,
                  endDate: newTaskEnd || task?.endDate
                });
              }
              // Shift subtasks under this task
              const taskSubs = deliverables?.filter((d) => d?.rowType === 'subtask' && d?.parentId === task?.id);
              for (const sub of taskSubs) {
                const newSubStart = sub?.startDate ?
                format(addDays(new Date(sub.startDate), dateShiftDays), 'yyyy-MM-dd') :
                null;
                const newSubEnd = sub?.endDate ?
                format(addDays(new Date(sub.endDate), dateShiftDays), 'yyyy-MM-dd') :
                null;
                if (newSubStart || newSubEnd) {
                  await projectService?.updateDeliverable(sub?.id, {
                    startDate: newSubStart || sub?.startDate,
                    endDate: newSubEnd || sub?.endDate
                  });
                }
              }
            }
          }
        }

        // Cascade: shift future phases and their children
        if (dateShiftDays !== 0 && shiftFuturePhases) {
          const futurePhases = phases?.filter(
            (p) => p?.id !== phaseModalTarget?.id &&
            p?.startDate &&
            originalPhase?.startDate &&
            new Date(p.startDate) > new Date(originalPhase.startDate)
          );
          for (const fp of futurePhases) {
            const newFpStart = fp?.startDate ?
            format(addDays(new Date(fp.startDate), dateShiftDays), 'yyyy-MM-dd') :
            null;
            const newFpEnd = fp?.endDate ?
            format(addDays(new Date(fp.endDate), dateShiftDays), 'yyyy-MM-dd') :
            null;
            await projectService?.updatePhase(fp?.id, {
              startDate: newFpStart || fp?.startDate,
              endDate: newFpEnd || fp?.endDate,
              projectId
            });
            // Also shift all deliverables/tasks/subtasks in this future phase
            const fpDeliverables = deliverables?.filter(
              (d) => d?.phaseId === fp?.id && (!d?.rowType || d?.rowType === 'deliverable')
            );
            for (const del of fpDeliverables) {
              const newDelStart = del?.startDate ?
              format(addDays(new Date(del.startDate), dateShiftDays), 'yyyy-MM-dd') :
              null;
              const newDelEnd = del?.endDate ?
              format(addDays(new Date(del.endDate), dateShiftDays), 'yyyy-MM-dd') :
              null;
              if (newDelStart || newDelEnd) {
                await projectService?.updateDeliverable(del?.id, {
                  startDate: newDelStart || del?.startDate,
                  endDate: newDelEnd || del?.endDate
                });
              }
              // Shift tasks under this deliverable
              const delTasks = deliverables?.filter((d) => d?.rowType === 'task' && d?.parentId === del?.id);
              for (const task of delTasks) {
                const newTaskStart = task?.startDate ?
                format(addDays(new Date(task.startDate), dateShiftDays), 'yyyy-MM-dd') :
                null;
                const newTaskEnd = task?.endDate ?
                format(addDays(new Date(task.endDate), dateShiftDays), 'yyyy-MM-dd') :
                null;
                if (newTaskStart || newTaskEnd) {
                  await projectService?.updateDeliverable(task?.id, {
                    startDate: newTaskStart || task?.startDate,
                    endDate: newTaskEnd || task?.endDate
                  });
                }
                // Shift subtasks under this task
                const taskSubs = deliverables?.filter((d) => d?.rowType === 'subtask' && d?.parentId === task?.id);
                for (const sub of taskSubs) {
                  const newSubStart = sub?.startDate ?
                  format(addDays(new Date(sub.startDate), dateShiftDays), 'yyyy-MM-dd') :
                  null;
                  const newSubEnd = sub?.endDate ?
                  format(addDays(new Date(sub.endDate), dateShiftDays), 'yyyy-MM-dd') :
                  null;
                  if (newSubStart || newSubEnd) {
                    await projectService?.updateDeliverable(sub?.id, {
                      startDate: newSubStart || sub?.startDate,
                      endDate: newSubEnd || sub?.endDate
                    });
                  }
                }
              }
            }
          }
        }

        await loadPhases();
        await loadDeliverables(true);
        showNotification('Phase updated successfully');
      }
      setPhaseModalOpen(false);
      setPhaseModalTarget(null);
    } catch (err) {
      showNotification(err?.message || 'Failed to save phase', 'error');
    } finally {
      setSavingEditPhase(false);
    }
  };

  const handlePhaseModalDelete = async (phaseId, orphanAction) => {
    try {
      if (orphanAction === 'delete') {
        // Delete all deliverables (and their tasks/subtasks) in this phase
        const phaseDeliverables = deliverables?.filter(
          (d) => d?.phaseId === phaseId && (!d?.rowType || d?.rowType === 'deliverable')
        );
        for (const del of phaseDeliverables) {
          // Tasks and subtasks are cascade-deleted by the service or DB
          await projectService?.deleteDeliverable(del?.id);
        }
      } else {
        // Unassign: remove phaseId from all deliverables in this phase
        const phaseDeliverables = deliverables?.filter(
          (d) => d?.phaseId === phaseId && (!d?.rowType || d?.rowType === 'deliverable')
        );
        for (const del of phaseDeliverables) {
          await projectService?.updateDeliverable(del?.id, { phaseId: null });
        }
      }
      const { error } = await projectService?.deletePhase(phaseId);
      if (error) throw error instanceof Error ? error : new Error(error?.message || 'Failed to delete phase');
      await loadPhases();
      await loadDeliverables(true);
      showNotification('Phase deleted successfully');
      setPhaseModalOpen(false);
      setPhaseModalTarget(null);
    } catch (err) {
      showNotification(err?.message || 'Failed to delete phase', 'error');
    }
  };

  const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(amount);

  const formatDate = (dateString) =>
  dateString ? formatDisplayDate(dateString) : 'N/A';

  // Calculate project progress: each phase, deliverable, task, and subtask = 1 point
  const projectProgress = (() => {
    const allItems = [
    ...(phases || []),
    ...(deliverables || [])];

    const total = allItems?.length;
    if (total === 0) return 0;
    const completed = allItems?.filter(
      (item) => item?.status === 'Completed' || item?.status === 'completed'
    )?.length;
    return Math.round(completed / total * 100);
  })();

  const infoTabs = [
  { id: 'overview', label: 'Overview', icon: 'Info' },
  { id: 'phases', label: 'Phases', icon: 'GitBranch' },
  { id: 'financial', label: 'Financial', icon: 'DollarSign' },
  { id: 'resources', label: 'Resources', icon: 'Users' }];


  const customerOptions = customers?.map((c) => ({ value: c?.company_name, label: c?.company_name }));
  const pmOptions = pmUsers?.map((pm) => ({ value: pm?.id, label: pm?.nickname || pm?.full_name || pm?.email }));
  const statusOptions = [
  { value: 'Planning', label: 'Planning' },
  { value: 'Bidding', label: 'Bidding' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'On Hold', label: 'On Hold' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' }];


  // ── Load Notes ────────────────────────────────────────────────────────────
  const loadNotes = async () => {
    if (!projectId) return;
    setNotesLoading(true);
    try {
      const data = await projectNoteService?.getByProject(projectId);
      setNotes(data);
    } catch (err) {
      console.error('Failed to load notes:', err);
    } finally {
      setNotesLoading(false);
    }
  };

  // ── Load Documents ────────────────────────────────────────────────────────
  const loadDocuments = async () => {
    if (!projectId) return;
    setDocumentsLoading(true);
    try {
      const data = await projectDocumentService?.getByProject(projectId);
      setDocuments(data);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  // ── Note handlers ─────────────────────────────────────────────────────────
  const handleAddNote = async () => {
    if (!newNoteTitle?.trim() || savingNote) return;
    setSavingNote(true);
    try {
      const newNote = await projectNoteService?.create(projectId, newNoteTitle?.trim(), newNoteContent?.trim(), userProfile?.id);
      setNotes((prev) => [newNote, ...prev]);
      setNewNoteTitle('');
      setNewNoteContent('');
      setShowAddNoteModal(false);
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveEditNote = async () => {
    if (!editNoteTitle?.trim() || savingNote) return;
    setSavingNote(true);
    try {
      const updated = await projectNoteService?.update(editingNote?.id, editNoteTitle?.trim(), editNoteText?.trim(), userProfile?.id);
      setNotes((prev) => prev?.map((n) => n?.id === updated?.id ? updated : n));
      setEditingNote(null);
      setEditNoteTitle('');
      setEditNoteText('');
    } catch (err) {
      console.error('Failed to update note:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    setDeletingNoteId(noteId);
    try {
      await projectNoteService?.delete(noteId);
      setNotes((prev) => prev?.filter((n) => n?.id !== noteId));
      if (editingNote?.id === noteId) {
        setEditingNote(null);
        setEditNoteTitle('');
        setEditNoteText('');
      }
    } catch (err) {
      console.error('Failed to delete note:', err);
    } finally {
      setDeletingNoteId(null);
    }
  };

  // ── Document handlers ─────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || uploadingDoc) return;
    setUploadingDoc(true);
    try {
      const doc = await projectDocumentService?.uploadFile(projectId, file, userProfile?.id);
      setDocuments((prev) => [doc, ...prev]);
    } catch (err) {
      console.error('Failed to upload file:', err);
    } finally {
      setUploadingDoc(false);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const handleAddLink = async () => {
    if (!linkName?.trim() || !linkUrl?.trim() || savingLink) return;
    setSavingLink(true);
    try {
      const doc = await projectDocumentService?.addLink(projectId, linkName?.trim(), linkUrl?.trim(), userProfile?.id);
      setDocuments((prev) => [doc, ...prev]);
      setLinkName('');
      setLinkUrl('');
      setShowLinkForm(false);
    } catch (err) {
      console.error('Failed to add link:', err);
    } finally {
      setSavingLink(false);
    }
  };

  const handleOpenDocument = async (doc) => {
    if (doc?.is_link) {
      let url = doc?.link_url;
      if (url && !url?.startsWith('http')) url = `https://${url}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const signedUrl = await projectDocumentService?.getSignedUrl(doc?.file_path);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open document:', err);
    }
  };

  const handleDeleteDocument = async (doc) => {
    setDeletingDocId(doc?.id);
    try {
      await projectDocumentService?.delete(doc);
      setDocuments((prev) => prev?.filter((d) => d?.id !== doc?.id));
      if (editingDoc?.id === doc?.id) {
        setEditingDoc(null);
        setEditDocTitle('');
        setEditDocNote('');
        setEditDocSource('');
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
    } finally {
      setDeletingDocId(null);
    }
  };

  // ── Add Document handler ───────────────────────────────────────────────────
  const handleAddDocument = async () => {
    if (!newDocTitle?.trim() || savingDoc) return;
    setSavingDoc(true);
    try {
      const doc = await projectDocumentService?.addDocument(projectId, {
        name: newDocTitle?.trim(),
        documentNote: newDocNote?.trim(),
        documentSource: newDocSource?.trim()
      }, userProfile?.id);
      setDocuments((prev) => [doc, ...prev]);
      setNewDocTitle('');
      setNewDocNote('');
      setNewDocSource('');
      setShowAddDocModal(false);
    } catch (err) {
      console.error('Failed to add document:', err);
    } finally {
      setSavingDoc(false);
    }
  };

  // ── Save Edit Document handler ─────────────────────────────────────────────
  const handleSaveEditDocument = async () => {
    if (!editDocTitle?.trim() || savingDoc) return;
    setSavingDoc(true);
    try {
      const updated = await projectDocumentService?.updateDocument(editingDoc?.id, {
        name: editDocTitle?.trim(),
        documentNote: editDocNote?.trim(),
        documentSource: editDocSource?.trim()
      });
      setDocuments((prev) => prev?.map((d) => d?.id === updated?.id ? updated : d));
      setEditingDoc(null);
      setEditDocTitle('');
      setEditDocNote('');
      setEditDocSource('');
    } catch (err) {
      console.error('Failed to update document:', err);
    } finally {
      setSavingDoc(false);
    }
  };

  // ── Load Images ───────────────────────────────────────────────────────────
  const loadImages = async () => {
    if (!projectId) return;
    setImagesLoading(true);
    try {
      const data = await projectImageService?.getByProject(projectId);
      setImages(data);
    } catch (err) {
      console.error('Failed to load images:', err);
    } finally {
      setImagesLoading(false);
    }
  };

  // ── Load Change Orders ────────────────────────────────────────────────────
  const loadChangeOrders = async () => {
    if (!projectId) return;
    setChangeOrdersLoading(true);
    try {
      const data = await projectChangeOrderService?.getByProject(projectId);
      setChangeOrders(data);
    } catch (err) {
      console.error('Failed to load change orders:', err);
    } finally {
      setChangeOrdersLoading(false);
    }
  };

  // ── Change Order handlers ─────────────────────────────────────────────────
  const handleAddChangeOrder = async () => {
    if (!newCoTitle?.trim() || savingChangeOrder) return;
    setSavingChangeOrder(true);
    try {
      const co = await projectChangeOrderService?.create(projectId, {
        title: newCoTitle?.trim(),
        description: newCoDescription?.trim(),
        impact: newCoImpact?.trim()
      }, userProfile?.id);
      setChangeOrders((prev) => [co, ...prev]);
      setNewCoTitle('');
      setNewCoDescription('');
      setNewCoImpact('');
      setShowAddChangeOrderModal(false);
    } catch (err) {
      console.error('Failed to add change order:', err);
    } finally {
      setSavingChangeOrder(false);
    }
  };

  const handleSaveEditChangeOrder = async () => {
    if (!editCoTitle?.trim() || savingChangeOrder) return;
    setSavingChangeOrder(true);
    try {
      const updated = await projectChangeOrderService?.update(editingChangeOrder?.id, {
        title: editCoTitle?.trim(),
        description: editCoDescription?.trim(),
        impact: editCoImpact?.trim()
      }, userProfile?.id);
      setChangeOrders((prev) => prev?.map((co) => co?.id === updated?.id ? updated : co));
      setEditingChangeOrder(null);
      setEditCoTitle('');
      setEditCoDescription('');
      setEditCoImpact('');
    } catch (err) {
      console.error('Failed to update change order:', err);
    } finally {
      setSavingChangeOrder(false);
    }
  };

  const handleDeleteChangeOrder = async (co) => {
    setDeletingChangeOrderId(co?.id);
    try {
      await projectChangeOrderService?.delete(co?.id);
      setChangeOrders((prev) => prev?.filter((c) => c?.id !== co?.id));
      if (editingChangeOrder?.id === co?.id) {
        setEditingChangeOrder(null);
        setEditCoTitle('');
        setEditCoDescription('');
        setEditCoImpact('');
      }
    } catch (err) {
      console.error('Failed to delete change order:', err);
    } finally {
      setDeletingChangeOrderId(null);
    }
  };

  // ── Image handlers ────────────────────────────────────────────────────────
  const handleAddImage = async () => {
    if (!newImageTitle?.trim() || savingImg) return;
    setSavingImg(true);
    try {
      let img;
      if ((newImageInputMode === 'upload' || newImageInputMode === 'camera') && newImageFile) {
        img = await projectImageService?.uploadFile(projectId, newImageFile, userProfile?.id, newImageTitle?.trim(), newImageNote?.trim());
      } else if (newImageInputMode === 'paste' && newImagePasteFile) {
        img = await projectImageService?.uploadFile(projectId, newImagePasteFile, userProfile?.id, newImageTitle?.trim(), newImageNote?.trim());
      } else {
        const imageUrl = newImageInputMode === 'paste' ? newImagePasteData?.trim() : newImageUrl?.trim();
        img = await projectImageService?.addImage(projectId, {
          title: newImageTitle?.trim(),
          imageNote: newImageNote?.trim(),
          imageUrl: imageUrl || null
        }, userProfile?.id);
      }
      setImages((prev) => [img, ...prev]);
      setNewImageTitle('');
      setNewImageNote('');
      setNewImageUrl('');
      setNewImageFile(null);
      setNewImagePasteData('');
      setNewImagePasteFile(null);
      setNewImagePastePreview('');
      setNewImageInputMode('url');
      setShowAddImageModal(false);
    } catch (err) {
      console.error('Failed to add image:', err);
    } finally {
      setSavingImg(false);
    }
  };

  const handleSaveEditImage = async () => {
    if (!editImageTitle?.trim() || savingImg) return;
    setSavingImg(true);
    try {
      const updated = await projectImageService?.updateImage(editingImage?.id, {
        title: editImageTitle?.trim(),
        imageNote: editImageNote?.trim(),
        imageUrl: editImageUrl?.trim() || null
      });
      setImages((prev) => prev?.map((img) => img?.id === updated?.id ? updated : img));
      setEditingImage(null);
      setEditImageTitle('');
      setEditImageNote('');
      setEditImageUrl('');
    } catch (err) {
      console.error('Failed to update image:', err);
    } finally {
      setSavingImg(false);
    }
  };

  const handleDeleteImage = async (image) => {
    setDeletingImageId(image?.id);
    try {
      await projectImageService?.delete(image);
      setImages((prev) => prev?.filter((img) => img?.id !== image?.id));
      if (editingImage?.id === image?.id) {
        setEditingImage(null);
        setEditImageTitle('');
        setEditImageNote('');
        setEditImageUrl('');
      }
    } catch (err) {
      console.error('Failed to delete image:', err);
    } finally {
      setDeletingImageId(null);
    }
  };

  // ── Helper: format display name ───────────────────────────────────────────
  const getDisplayName = (profile) => {
    if (!profile) return 'Unknown';
    if (profile?.full_name) return profile?.full_name;
    const first = profile?.first_name || '';
    const last = profile?.last_name || '';
    if (first || last) return `${first} ${last}`?.trim();
    return profile?.email || 'Unknown';
  };

  const formatNoteDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mma');
    } catch {
      return dateStr;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return 'File';
    if (mimeType?.includes('pdf')) return 'FileText';
    if (mimeType?.includes('image')) return 'Image';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'FileText';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet') || mimeType?.includes('csv')) return 'Table';
    if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation')) return 'Presentation';
    return 'File';
  };

  if (!projectId) return null;

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Inline Notification */}
        {notification &&
        <div className={`fixed top-4 right-4 z-[300] flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border transition-all ${
        notification?.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`
        }>
            <Icon name={notification?.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={18} />
            <span className="text-sm font-medium">{notification?.message}</span>
            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-70">
              <Icon name="X" size={14} />
            </button>
          </div>
        }

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm &&
        <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/50">
            <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                  <Icon name="Trash2" size={20} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Delete Project</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-6">
                Are you sure you want to permanently delete{' '}
                <span className="font-semibold">{project?.projectName}</span>? All associated data will be removed.
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteProject}
                disabled={isDeleting}
                iconName={isDeleting ? 'Loader2' : 'Trash2'}
                iconPosition="left">

                  {isDeleting ? 'Deleting...' : 'Delete Project'}
                </Button>
              </div>
            </div>
          </div>
        }

        {/* Complete Children Confirmation Dialog */}
        {completeChildrenDialog &&
        <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/50">
            <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 flex-shrink-0">
                  <Icon name="CheckCircle2" size={20} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Complete All Children?</h3>
                  <p className="text-sm text-muted-foreground">
                    {completeChildrenDialog?.type === 'phase' ? 'Phase' : completeChildrenDialog?.type === 'deliverable' ? 'Deliverable' : 'Task'} marked as Completed
                  </p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-6">
                Would you like to also mark all{' '}
                {completeChildrenDialog?.type === 'phase' ? 'Deliverables, Tasks, and Sub-tasks' :
              completeChildrenDialog?.type === 'deliverable' ? 'Tasks and Sub-tasks' : 'Sub-tasks'}{' '}
                under <span className="font-semibold">{completeChildrenDialog?.name}</span> as Completed?
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button variant="ghost" size="sm" onClick={() => setCompleteChildrenDialog(null)}>
                  No, Keep As-Is
                </Button>
                <Button
                variant="default"
                size="sm"
                onClick={handleCompleteChildrenConfirm}
                iconName="CheckCircle2"
                iconPosition="left">

                  Yes, Complete All
                </Button>
              </div>
            </div>
          </div>
        }

        {/* Edit Phase Modal */}
        {editPhaseModal &&
        <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/50">
            <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Icon name="GitBranch" size={18} className="text-accent" />
                  <h3 className="text-base font-semibold text-foreground">Edit Phase</h3>
                </div>
                <button onClick={() => setEditPhaseModal(null)} className="text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Phase Name <span className="text-red-500">*</span></label>
                  <input
                  type="text"
                  value={editPhaseForm?.name || ''}
                  onChange={(e) => setEditPhaseForm((prev) => ({ ...prev, name: e?.target?.value }))}
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent ${editPhaseErrors?.name ? 'border-red-400' : 'border-border'}`}
                  placeholder="Enter phase name" />
                  {editPhaseErrors?.name && <p className="text-xs text-red-500 mt-1">{editPhaseErrors?.name}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Start Date <span className="text-red-500">*</span></label>
                    <input
                    type="date"
                    value={editPhaseForm?.startDate || ''}
                    onChange={(e) => setEditPhaseForm((prev) => ({ ...prev, startDate: e?.target?.value }))}
                    className={`w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent ${editPhaseErrors?.startDate ? 'border-red-400' : 'border-border'}`} />
                    {editPhaseErrors?.startDate && <p className="text-xs text-red-500 mt-1">{editPhaseErrors?.startDate}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">End Date <span className="text-red-500">*</span></label>
                    <input
                    type="date"
                    value={editPhaseForm?.endDate || ''}
                    onChange={(e) => setEditPhaseForm((prev) => ({ ...prev, endDate: e?.target?.value }))}
                    className={`w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent ${editPhaseErrors?.endDate ? 'border-red-400' : 'border-border'}`} />
                    {editPhaseErrors?.endDate && <p className="text-xs text-red-500 mt-1">{editPhaseErrors?.endDate}</p>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                  <select
                  value={editPhaseForm?.status || 'Planning'}
                  onChange={(e) => setEditPhaseForm((prev) => ({ ...prev, status: e?.target?.value }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent">
                    <option value="Planning">Planning</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 mt-5">
                <Button variant="ghost" size="sm" onClick={() => setEditPhaseModal(null)} disabled={savingEditPhase}>
                  Cancel
                </Button>
                <Button
                variant="default"
                size="sm"
                onClick={handleSaveEditPhase}
                disabled={savingEditPhase}
                iconName={savingEditPhase ? 'Loader2' : 'Save'}
                iconPosition="left">
                  {savingEditPhase ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        }

        {/* Phase Active Deliverables Confirmation Dialog */}
        {phaseActionConfirm &&
        <div className="fixed inset-0 z-[450] flex items-center justify-center bg-black/50">
            <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 flex-shrink-0">
                  <Icon name="AlertTriangle" size={20} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    {phaseActionConfirm?.action === 'edit' ? 'Edit Phase with Active Deliverables?' : 'Delete Phase with Active Deliverables?'}
                  </h3>
                  <p className="text-sm text-muted-foreground">Data loss prevention</p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-2">
                The phase <span className="font-semibold">"{phaseActionConfirm?.phase?.name}"</span> contains{' '}
                <span className="font-semibold text-amber-600">{phaseActionConfirm?.activeCount} active deliverable{phaseActionConfirm?.activeCount !== 1 ? 's' : ''}</span> that are not yet completed.
              </p>
              <p className="text-sm text-muted-foreground/60 mb-5">
                {phaseActionConfirm?.action === 'edit' ? 'Editing this phase (including date changes) may affect the schedule of these deliverables. Are you sure you want to proceed?' : 'Deleting this phase will affect all active deliverables assigned to it. You will be asked how to handle them. Are you sure you want to proceed?'}
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button variant="ghost" size="sm" onClick={() => setPhaseActionConfirm(null)}>
                  Cancel
                </Button>
                <Button
                variant={phaseActionConfirm?.action === 'delete' ? 'destructive' : 'default'}
                size="sm"
                onClick={handlePhaseActionConfirmProceed}
                iconName={phaseActionConfirm?.action === 'edit' ? 'Edit' : 'Trash2'}
                iconPosition="left">
                  {phaseActionConfirm?.action === 'edit' ? 'Proceed with Edit' : 'Proceed with Delete'}
                </Button>
              </div>
            </div>
          </div>
        }

        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-2 mt-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => navigate('/project-management')} className="shrink-0 h-7 w-7 mt-3">
                <Icon name="ArrowLeft" size={18} />
              </Button>
              <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap mt-3">
                <h1 className="text-base font-bold text-foreground truncate">
                  {project?.projectName || 'Project Management'}
                </h1>
                {project &&
                <>
                    <span className="text-muted-foreground hidden sm:inline">·</span>
                    <span className="text-xs text-muted-foreground data-text hidden sm:inline">{project?.projectNumber}</span>
                    <span className="text-muted-foreground hidden sm:inline">·</span>
                    <ProjectStatusBadge status={project?.status} />
                  </>
                }
              </div>
              {!isEditing ?
              <div className="flex items-center gap-1.5 shrink-0">
                  {isAdmin &&
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2 text-xs"
                  iconName="Trash2"
                  iconPosition="left">

                      Delete
                    </Button>
                }
                  <Button variant="outline" size="sm" onClick={handleStartEdit} iconName="Edit" iconPosition="left" className="h-7 px-2 text-xs">
                    Edit
                  </Button>
                </div> :

              <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="ghost" size="sm" onClick={handleCancelEdit} disabled={isSaving} className="h-7 px-2 text-xs">
                    Cancel
                  </Button>
                  <Button variant="default" size="sm" onClick={handleSaveEdit} disabled={isSaving} iconName={isSaving ? 'Loader2' : 'Save'} iconPosition="left" className="h-7 px-2 text-xs">
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              }
            </div>
          </div>
        </div>

        {/* ── TOP SECTION: 3 Compact Info Boxes ── */}
        <div className="bg-card border-b border-border">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            {/* Collapse toggle bar */}
            <button
              onClick={() => setInfoSectionCollapsed((prev) => !prev)}
              className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-muted/30 transition-colors group">

              <Icon
                name={infoSectionCollapsed ? 'ChevronRight' : 'ChevronDown'}
                size={14}
                className="text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />

              <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                {infoSectionCollapsed ? 'Show Project Notes and Documents' : 'Hide Project Notes and Documents'}
              </span>
            </button>

            {!infoSectionCollapsed &&
            <div className="pb-3">
                {isEditing ?
              <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input label="Project Name" type="text" placeholder="Enter project name" value={editForm?.projectName || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, projectName: e?.target?.value }))} error={editErrors?.projectName} required />
                      <Select label="Customer" options={customerOptions} value={editForm?.customerName || ''} onChange={(value) => setEditForm((prev) => ({ ...prev, customerName: value }))} placeholder="Select customer" error={editErrors?.customerName} required searchable />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input label="Start Date" type="date" value={editForm?.startDate || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, startDate: e?.target?.value }))} error={editErrors?.startDate} required />
                      <Input label="End Date" type="date" value={editForm?.endDate || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, endDate: e?.target?.value }))} error={editErrors?.endDate} required />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input label="Total Budget ($)" type="number" placeholder="Enter budget amount" value={editForm?.totalBudget || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, totalBudget: e?.target?.value }))} error={editErrors?.totalBudget} required />
                      <Input label="Location" type="text" placeholder="Enter project location" value={editForm?.location || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, location: e?.target?.value }))} error={editErrors?.location} required />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Select label="Status" options={statusOptions} value={editForm?.status || 'Planning'} onChange={(value) => setEditForm((prev) => ({ ...prev, status: value }))} placeholder="Select status" />
                      <Select label="Assigned PM" options={pmOptions} value={editForm?.assignedPmId || ''} onChange={(value) => setEditForm((prev) => ({ ...prev, assignedPmId: value }))} placeholder="Select project manager" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                      <textarea rows={2} placeholder="Enter project description" value={editForm?.description || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, description: e?.target?.value }))} className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none" />
                    </div>
                  </div> :

              project &&
              <div ref={containerRef} className="flex gap-0 items-stretch w-full overflow-hidden">
                    {/* Box 1: Overview */}
                    <div
                  className="bg-muted/40 border border-border rounded-lg p-3 overflow-hidden"
                  style={{ width: `${boxWidths?.[0]}%`, minWidth: '10%', flexShrink: 0 }}>

                      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-border/60">
                        <Icon name="Info" size={13} className="text-accent" />
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Overview</span>
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground shrink-0">Customer</span>
                          <span className="text-xs font-medium text-foreground text-right truncate">{project?.customerName || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground shrink-0">Location</span>
                          <span className="text-xs font-medium text-foreground text-right truncate">{project?.location || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground shrink-0">Start</span>
                          <span className="text-xs font-semibold text-foreground data-text">{formatDate(project?.startDate)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground shrink-0">End</span>
                          <span className="text-xs font-semibold text-foreground data-text">{formatDate(project?.endDate)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground shrink-0">Total Budget</span>
                          <span className="text-xs font-semibold text-foreground data-text">{formatCurrency(project?.totalBudget)}</span>
                        </div>
                        <div className="pt-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Progress</span>
                            <span className="text-xs font-semibold text-foreground">{projectProgress}%</span>
                          </div>
                          <ProjectProgressBar percentage={projectProgress} size="sm" />
                        </div>
                        {project?.description &&
                    <div className="pt-1 border-t border-border/60">
                            <p className="text-xs text-muted-foreground line-clamp-2">{project?.description}</p>
                          </div>
                    }
                      </div>
                    </div>

                    {/* Drag Divider 0 */}
                    <div
                  className="flex items-center justify-center w-2 mx-1 cursor-col-resize group shrink-0 select-none"
                  onMouseDown={(e) => handleDividerMouseDown(0, e)}
                  title="Drag to resize">

                      <div className="w-0.5 h-full bg-border group-hover:bg-accent transition-colors rounded-full" />
                    </div>

                    {/* Box 2: Phases */}
                    <div
                  className="bg-muted/40 border border-border rounded-lg p-3 overflow-hidden"
                  style={{ width: `${boxWidths?.[1]}%`, minWidth: '10%', flexShrink: 0 }}>

                      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-border/60">
                        <Icon name="GitBranch" size={13} className="text-accent" />
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Phases</span>
                        <span className="ml-auto text-xs text-muted-foreground">{phases?.length || 0} total</span>
                        <button
                      onClick={() => setAddPhasesModalOpen(true)}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-accent/10 hover:bg-accent/20 text-accent text-xs font-medium transition-colors ml-1"
                      title="Add phases">

                          <Icon name="Plus" size={11} />
                          Add
                        </button>
                      </div>
                      {phases?.length > 0 ?
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                          {phases?.map((phase) =>
                    <div key={phase?.id} className="py-1 border-b border-border/30 last:border-0">
                              {/* Single row: name | dates | status */}
                              <div className="flex items-center gap-1.5 min-w-0">
                                {/* Phase Name */}
                                {editingPhaseName?.id === phase?.id ?
                        <div className="flex items-center gap-1 min-w-0 flex-shrink-0" style={{ maxWidth: '35%' }}>
                                    <input
                            autoFocus
                            type="text"
                            value={editingPhaseName?.value}
                            onChange={(e) => setEditingPhaseName({ id: phase?.id, value: e?.target?.value })}
                            onBlur={() => handlePhaseNameSave(phase?.id)}
                            onKeyDown={(e) => handlePhaseNameKeyDown(e, phase?.id)}
                            className="text-xs font-semibold text-foreground bg-background border border-ring rounded px-1 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-ring" />

                                    {savingPhaseName === phase?.id &&
                          <Icon name="Loader2" size={11} className="text-accent animate-spin shrink-0" />
                          }
                                  </div> :

                        <span
                          className="text-xs font-semibold text-foreground truncate cursor-pointer hover:text-accent transition-colors shrink-0"
                          style={{ maxWidth: '35%' }}
                          title="Double-click to edit name"
                          onDoubleClick={() => handlePhaseNameDoubleClick(phase)}>

                                    {phase?.name}
                                  </span>
                        }

                                {/* Dates inline */}
                                <div className="flex items-center gap-0.5 text-xs text-muted-foreground shrink-0">
                                  {/* Start Date */}
                                  {editingPhaseDate?.id === phase?.id && editingPhaseDate?.field === 'startDate' ?
                          <div className="flex items-center gap-0.5">
                                      <input
                              autoFocus
                              type="date"
                              value={editingPhaseDate?.value || ''}
                              onChange={(e) => setEditingPhaseDate((prev) => ({ ...prev, value: e?.target?.value }))}
                              onBlur={() => handlePhaseDateSave(phase?.id)}
                              onKeyDown={(e) => handlePhaseDateKeyDown(e, phase?.id)}
                              className="text-xs bg-background border border-ring rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-ring w-28" />

                                      {savingPhaseDate === phase?.id &&
                            <Icon name="Loader2" size={11} className="text-accent animate-spin shrink-0" />
                            }
                                    </div> :

                          <span
                            className="cursor-pointer hover:text-accent hover:underline transition-colors"
                            title="Double-click to edit start date"
                            onDoubleClick={() => handlePhaseDateDoubleClick(phase, 'startDate')}>

                                      {formatDate(phase?.startDate)}
                                    </span>
                          }
                                  <span className="mx-0.5">→</span>
                                  {/* End Date */}
                                  {editingPhaseDate?.id === phase?.id && editingPhaseDate?.field === 'endDate' ?
                          <div className="flex items-center gap-0.5">
                                      <input
                              autoFocus
                              type="date"
                              value={editingPhaseDate?.value || ''}
                              onChange={(e) => setEditingPhaseDate((prev) => ({ ...prev, value: e?.target?.value }))}
                              onBlur={() => handlePhaseDateSave(phase?.id)}
                              onKeyDown={(e) => handlePhaseDateKeyDown(e, phase?.id)}
                              className="text-xs bg-background border border-ring rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-ring w-28" />

                                      {savingPhaseDate === phase?.id &&
                            <Icon name="Loader2" size={11} className="text-accent animate-spin shrink-0" />
                            }
                                    </div> :

                          <span
                            className="cursor-pointer hover:text-accent hover:underline transition-colors"
                            title="Double-click to edit end date"
                            onDoubleClick={() => handlePhaseDateDoubleClick(phase, 'endDate')}>

                                      {formatDate(phase?.endDate)}
                                    </span>
                          }
                                </div>

                              </div>
                            </div>
                    )}
                        </div> :

                  <div className="flex items-center gap-1.5 py-1 text-muted-foreground">
                          <Icon name="GitBranch" size={13} className="opacity-40" />
                          <span className="text-xs">No phases defined</span>
                        </div>
                  }
                    </div>

                    {/* Drag Divider 1 */}
                    <div
                  className="flex items-center justify-center w-2 mx-1 cursor-col-resize group shrink-0 select-none"
                  onMouseDown={(e) => handleDividerMouseDown(1, e)}
                  title="Drag to resize">

                      <div className="w-0.5 h-full bg-border group-hover:bg-accent transition-colors rounded-full" />
                    </div>

                    {/* Box 3: Team &amp; Resources */}
                    <div
                  className="bg-muted/40 border border-border rounded-lg p-3 overflow-hidden"
                  style={{ width: `${boxWidths?.[2]}%`, minWidth: '10%', flexShrink: 0 }}>

                      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-border/60">
                        <Icon name="Users" size={13} className="text-accent" />
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Team &amp; Resources</span>
                      </div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {/* PM Row — always first */}
                        <div className="flex items-center justify-between gap-2 pb-1.5 border-b border-border/60">
                          <span className="text-xs text-muted-foreground shrink-0">PM</span>
                          <span className="text-xs font-medium text-foreground text-right truncate">
                            {project?.assignedPm?.nickname || project?.assignedPm?.fullName || project?.assignedPm?.email || <span className="text-muted-foreground italic">Unassigned</span>}
                          </span>
                        </div>
                        {/* Assigned Resources sorted by category then start date */}
                        {(() => {
                      const getCatOrder = (cat) => {
                        const c = (cat || '')?.toLowerCase()?.replace(/s$/, '');
                        const order = ['employee', 'team', 'contractor', 'truck', 'equipment', 'trailer'];
                        const idx = order?.indexOf(c);
                        return idx === -1 ? 999 : idx;
                      };
                      const sorted = [...(projectAssignments || [])]?.sort((a, b) => {
                        const catDiff = getCatOrder(a?.resource?.category) - getCatOrder(b?.resource?.category);
                        if (catDiff !== 0) return catDiff;
                        const dateA = a?.startDate || a?.start_date || '';
                        const dateB = b?.startDate || b?.start_date || '';
                        return dateA?.localeCompare(dateB);
                      });

                      const conflictIds = new Set();
                      for (let i = 0; i < sorted?.length; i++) {
                        const a = sorted?.[i];
                        const aResourceId = a?.resource_id || a?.resource?.id;
                        if (!aResourceId) continue;
                        const aStart = a?.startDate || a?.start_date || '';
                        const aEnd = a?.endDate || a?.end_date || '';
                        const aStartD = aStart ? new Date(aStart) : null;
                        if (!aStartD) continue;
                        const aEndD = aEnd ? new Date(aEnd) : new Date('9999-12-31');

                        for (const other of allAssignments) {
                          if (other?.id === a?.id) continue;
                          if (other?.resource_id !== aResourceId) continue;
                          const bStart = other?.startDate;
                          const bEnd = other?.endDate;
                          const bStartD = bStart ? new Date(bStart) : null;
                          if (!bStartD) continue;
                          const bEndD = bEnd ? new Date(bEnd) : new Date('9999-12-31');
                          if (aStartD <= bEndD && bStartD <= aEndD) {
                            conflictIds?.add(`${a?.id}-${i}`);
                            break;
                          }
                        }
                      }

                      if (sorted?.length === 0) {
                        return (
                          <div className="flex items-center gap-1.5 py-1 text-muted-foreground">
                                <Icon name="Users" size={13} className="opacity-40" />
                                <span className="text-xs">No resources assigned</span>
                              </div>);
                      }
                      return sorted?.map((assignment, idx) => {
                        const startDate = assignment?.startDate || assignment?.start_date;
                        const endDate = assignment?.endDate || assignment?.end_date;
                        const isConflict = conflictIds?.has(`${assignment?.id}-${idx}`);
                        return (
                          <div key={`${assignment?.id}-${idx}`} className={`flex items-center justify-between gap-2 py-0.5 ${isConflict ? 'text-red-500' : ''}`}>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  {isConflict && <Icon name="AlertTriangle" size={11} className="text-red-500 shrink-0" />}
                                  <span className={`text-xs font-medium truncate ${isConflict ? 'text-red-500' : 'text-foreground'}`}>{assignment?.resource?.name || 'Unknown'}</span>
                                  <span className={`text-xs shrink-0 px-1 rounded ${isConflict ? 'text-red-400 bg-red-500/10' : 'text-muted-foreground bg-muted'}`}>{assignment?.resource?.category}</span>
                                </div>
                                <span className={`text-xs data-text shrink-0 ${isConflict ? 'text-red-400' : 'text-muted-foreground'}`}>
                                  {formatDate(startDate)}{endDate ? ` – ${formatDate(endDate)}` : ''}
                                </span>
                              </div>);
                      });
                    })()}
                      </div>
                    </div>

                    {/* Drag Divider 2 */}
                    <div
                  className="flex items-center justify-center w-2 mx-1 cursor-col-resize group shrink-0 select-none"
                  onMouseDown={(e) => handleDividerMouseDown(2, e)}
                  title="Drag to resize">

                      <div className="w-0.5 h-full bg-border group-hover:bg-accent transition-colors rounded-full" />
                    </div>

                    {/* Box 4: General Contractors, Sub-Contractors, and Vendors */}
                    <div
                  className="bg-muted/40 border border-border rounded-lg p-3 overflow-hidden"
                  style={{ width: `${boxWidths?.[3]}%`, minWidth: '10%', flexShrink: 0 }}>

                      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-border/60">
                        <Icon name="Building2" size={13} className="text-accent" />
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wide">SUB-CONTRACTORS, AND VENDORS</span>
                      </div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {/* General Contractors */}
                        {projectContractors?.generalContractors?.map((contractor) =>
                    <div key={contractor?.id} className="flex items-center justify-between gap-2 py-0.5">
                            <span className="text-xs font-medium text-foreground truncate flex-1 text-left">{contractor?.name}</span>
                            <span className="text-xs text-muted-foreground/70 shrink-0 whitespace-nowrap">{contractor?.role}</span>
                          </div>
                    )}

                        {/* Sub-Contractors */}
                        <div className="flex items-center justify-between gap-2 pb-1.5 border-b border-border/60">
                          <span className="text-xs text-muted-foreground shrink-0">Sub-Contractors</span>
                          <span className="text-xs font-medium text-foreground text-right truncate">
                            {projectContractors?.subContractors?.length || 0} total
                          </span>
                        </div>
                        {projectContractors?.subContractors?.map((contractor) =>
                    <div key={contractor?.id} className="flex items-center justify-between gap-2 py-0.5">
                            <span className="text-xs font-medium text-foreground truncate flex-1 text-left">{contractor?.name}</span>
                            <span className="text-xs text-muted-foreground/70 shrink-0 whitespace-nowrap">{contractor?.role}</span>
                          </div>
                    )}

                        {/* Vendors */}
                        <div className="flex items-center justify-between gap-2 py-0.5">
                          <span className="text-xs text-muted-foreground shrink-0">Vendors</span>
                          <span className="text-xs font-medium text-foreground text-right truncate">
                            {projectContractors?.vendors?.length || 0} total
                          </span>
                        </div>
                        {projectContractors?.vendors?.map((vendor) =>
                    <div key={vendor?.id} className="flex items-center justify-between gap-2 py-0.5">
                            <span className="text-xs font-medium text-foreground truncate flex-1 text-left">{vendor?.name}</span>
                            <span className="text-xs text-muted-foreground/70 shrink-0 whitespace-nowrap">{vendor?.role}</span>
                          </div>
                    )}
                      </div>
                    </div>
                  </div>
              }
              </div>
            }
          </div>
        </div>

        {/* ── PROJECT CONTENT SECTION: Notes, Documents, Images, Change Orders ── */}
        <div className="bg-card border-b border-border">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            {/* Collapse toggle bar */}
            <button
              onClick={() => setContentSectionCollapsed((prev) => !prev)}
              className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-muted/30 transition-colors group">
              <Icon
                name={contentSectionCollapsed ? 'ChevronRight' : 'ChevronDown'}
                size={14}
                className="text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
              <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                {contentSectionCollapsed ? 'Show Project Notes and Documents' : 'Hide Project Notes and Documents'}
              </span>
            </button>

            {!contentSectionCollapsed &&
            <div className="pb-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

                  {/* ── Project Notes ── */}
                  <div className="bg-background border border-border rounded-lg p-4 flex flex-col" style={{ minHeight: '280px' }}>
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
                      <Icon name="FileText" size={16} className="text-primary shrink-0" />
                      <h3 className="text-sm font-semibold text-foreground">Project Notes</h3>
                      {!notesLoading && notes?.length > 0 &&
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">{notes?.length}</span>
                    }
                      <button
                      onClick={() => {setNewNoteTitle('');setNewNoteContent('');setShowAddNoteModal(true);}}
                      className="ml-auto flex items-center gap-1 px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
                      title="Add Note">
                        <Icon name="Plus" size={12} />
                        Add
                      </button>
                    </div>

                    {/* Notes list — scrollable, fixed height, single-row: title left, date right */}
                    <div className="overflow-y-auto flex-1" style={{ maxHeight: '220px' }}>
                      {notesLoading ?
                    <div className="flex flex-col items-center justify-center py-6 gap-2">
                          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          <span className="text-xs text-muted-foreground">Loading notes…</span>
                        </div> :
                    notes?.length === 0 ?
                    <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Icon name="StickyNote" size={18} className="text-muted-foreground/50" />
                          </div>
                          <p className="text-xs text-muted-foreground">No notes yet</p>
                          <p className="text-xs text-muted-foreground/60">Click Add to create your first note</p>
                        </div> :

                    notes?.map((note) =>
                    <div
                      key={note?.id}
                      onDoubleClick={() => {setEditingNote(note);setEditNoteTitle(note?.title || '');setEditNoteText(note?.content || '');}}
                      className="flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer group transition-colors border-b border-border/30 last:border-b-0"
                      title="Double-click to edit">
                            <p className="text-xs font-medium text-foreground truncate flex-1 text-left">{note?.title || '(No title)'}</p>
                            <span className="text-xs text-muted-foreground/70 shrink-0 whitespace-nowrap">{note?.created_at ? format(new Date(note?.created_at), 'MM/dd/yy') : ''}</span>
                          </div>
                    )
                    }
                    </div>
                    {/* Detail button */}
                    <div className="flex justify-end mt-2 pt-2 border-t border-border/40">
                      <button
                      onClick={() => openSectionDetail('notes')}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-muted hover:text-foreground transition-colors">
                        <Icon name="ExternalLink" size={11} />
                        Detail
                      </button>
                    </div>
                  </div>

                  {/* ── Project Documents ── */}
                  <div className="bg-background border border-border rounded-lg p-4 flex flex-col" style={{ minHeight: '280px' }}>
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
                      <Icon name="FolderOpen" size={16} className="text-primary shrink-0" />
                      <h3 className="text-sm font-semibold text-foreground">Project Documents</h3>
                      {!documentsLoading && documents?.length > 0 &&
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">{documents?.length}</span>
                    }
                      <button
                      onClick={() => {setNewDocTitle('');setNewDocNote('');setNewDocSource('');setShowAddDocModal(true);}}
                      className="ml-auto flex items-center gap-1 px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
                      title="Add Document">
                        <Icon name="Plus" size={12} />
                        Add
                      </button>
                    </div>

                    {/* Documents list — scrollable, fixed height, single-row: title left, date right */}
                    <div className="overflow-y-auto flex-1" style={{ maxHeight: '220px' }}>
                      {documentsLoading ?
                    <div className="flex flex-col items-center justify-center py-6 gap-2">
                          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          <span className="text-xs text-muted-foreground">Loading documents…</span>
                        </div> :
                    documents?.length === 0 ?
                    <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Icon name="FolderOpen" size={18} className="text-muted-foreground/50" />
                          </div>
                          <p className="text-xs text-muted-foreground">No documents yet</p>
                          <p className="text-xs text-muted-foreground/60">Click Add to create your first document</p>
                        </div> :

                    documents?.map((doc) =>
                    <div
                      key={doc?.id}
                      onDoubleClick={() => {setEditingDoc(doc);setEditDocTitle(doc?.name || '');setEditDocNote(doc?.document_note || '');setEditDocSource(doc?.document_source || '');}}
                      className="flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer group transition-colors border-b border-border/30 last:border-b-0"
                      title="Double-click to edit">
                            <p className="text-xs font-medium text-foreground truncate flex-1 text-left">{doc?.name || '(No title)'}</p>
                            <span className="text-xs text-muted-foreground/70 shrink-0 whitespace-nowrap">{doc?.created_at ? format(new Date(doc?.created_at), 'MM/dd/yy') : ''}</span>
                          </div>
                    )
                    }
                    </div>
                    {/* Detail button */}
                    <div className="flex justify-end mt-2 pt-2 border-t border-border/40">
                      <button
                      onClick={() => openSectionDetail('documents')}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-muted hover:text-foreground transition-colors">
                        <Icon name="ExternalLink" size={11} />
                        Detail
                      </button>
                    </div>
                  </div>

                  {/* ── Project Images ── */}
                  <div className="bg-background border border-border rounded-lg p-4 flex flex-col" style={{ minHeight: '280px' }}>
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
                      <Icon name="Image" size={16} className="text-primary shrink-0" />
                      <h3 className="text-sm font-semibold text-foreground">Project Images</h3>
                      {!imagesLoading && images?.length > 0 &&
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">{images?.length}</span>
                    }
                      <button
                      onClick={() => {setNewImageTitle('');setNewImageNote('');setNewImageUrl('');setNewImageFile(null);setNewImagePasteData('');setNewImageInputMode('url');setShowAddImageModal(true);}}
                      className="ml-auto flex items-center gap-1 px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
                      title="Add Image">
                        <Icon name="Plus" size={12} />
                        Add
                      </button>
                    </div>

                    {/* Images list — scrollable, fixed height, single-row: title left, date right */}
                    <div className="overflow-y-auto flex-1" style={{ maxHeight: '220px' }}>
                      {imagesLoading ?
                    <div className="flex flex-col items-center justify-center py-6 gap-2">
                          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          <span className="text-xs text-muted-foreground">Loading images…</span>
                        </div> :
                    images?.length === 0 ?
                    <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Icon name="ImageOff" size={18} className="text-muted-foreground/50" />
                          </div>
                          <p className="text-xs text-muted-foreground">No images yet</p>
                          <p className="text-xs text-muted-foreground/60">Click Add to attach your first image</p>
                        </div> :

                    images?.map((img) =>
                    <div
                      key={img?.id}
                      onDoubleClick={async () => {
                        setEditingImage(img);
                        setEditImageTitle(img?.title || '');
                        setEditImageNote(img?.image_note || '');
                        setEditImageUrl(img?.image_url || '');
                        setEditImageSignedUrl('');
                        if (img?.is_upload && img?.file_path) {
                          try {
                            const signed = await projectImageService?.getSignedUrl(img?.file_path);
                            setEditImageSignedUrl(signed || '');
                          } catch (err) {
                            console.error('Failed to get signed URL:', err);
                          }
                        }
                      }}
                      className="flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer group transition-colors border-b border-border/30 last:border-b-0"
                      title="Double-click to edit">
                            <p className="text-xs font-medium text-foreground truncate flex-1 text-left">{img?.title || '(No title)'}</p>
                            <span className="text-xs text-muted-foreground/70 shrink-0 whitespace-nowrap">{img?.created_at ? format(new Date(img?.created_at), 'MM/dd/yy') : ''}</span>
                          </div>
                    )
                    }
                    </div>
                    {/* Detail button */}
                    <div className="flex justify-end mt-2 pt-2 border-t border-border/40">
                      <button
                      onClick={() => openSectionDetail('images')}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-muted hover:text-foreground transition-colors">
                        <Icon name="ExternalLink" size={11} />
                        Detail
                      </button>
                    </div>
                  </div>

                  {/* ── Change Orders ── */}
                  <div className="bg-background border border-border rounded-lg p-4 flex flex-col" style={{ minHeight: '280px' }}>
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
                      <Icon name="ClipboardList" size={16} className="text-primary shrink-0" />
                      <h3 className="text-sm font-semibold text-foreground">Change Orders</h3>
                      {!changeOrdersLoading && changeOrders?.length > 0 &&
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">{changeOrders?.length}</span>
                    }
                      <button
                      onClick={() => {setNewCoTitle('');setNewCoDescription('');setNewCoImpact('');setShowAddChangeOrderModal(true);}}
                      className="ml-auto flex items-center gap-1 px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
                      title="Add Change Order">
                        <Icon name="Plus" size={12} />
                        Add
                      </button>
                    </div>

                    {/* Change Orders list — scrollable, fixed height, single-row: title left, date right */}
                    <div className="overflow-y-auto flex-1" style={{ maxHeight: '220px' }}>
                      {changeOrdersLoading ?
                    <div className="flex flex-col items-center justify-center py-6 gap-2">
                          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          <span className="text-xs text-muted-foreground">Loading change orders…</span>
                        </div> :
                    changeOrders?.length === 0 ?
                    <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Icon name="ClipboardX" size={18} className="text-muted-foreground/50" />
                          </div>
                          <p className="text-xs text-muted-foreground">No change orders yet</p>
                          <p className="text-xs text-muted-foreground/60">Click Add to create your first change order</p>
                        </div> :

                    changeOrders?.map((co) =>
                    <div
                      key={co?.id}
                      onDoubleClick={() => {setEditingChangeOrder(co);setEditCoTitle(co?.title || '');setEditCoDescription(co?.description || '');setEditCoImpact(co?.impact || '');}}
                      className="flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer group transition-colors border-b border-border/30 last:border-b-0"
                      title="Double-click to edit">
                            <p className="text-xs font-medium text-foreground truncate flex-1 text-left">{co?.title || '(No title)'}</p>
                            <span className="text-xs text-muted-foreground/70 shrink-0 whitespace-nowrap">{co?.created_at ? format(new Date(co?.created_at), 'MM/dd/yy') : ''}</span>
                          </div>
                    )
                    }
                    </div>
                    {/* Detail button */}
                    <div className="flex justify-end mt-2 pt-2 border-t border-border/40">
                      <button
                      onClick={() => openSectionDetail('changeOrders')}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-muted hover:text-foreground transition-colors">
                        <Icon name="ExternalLink" size={11} />
                        Detail
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            }
          </div>
        </div>

        {/* ── BOTTOM SECTION: Project Detail / Gantt / Card View ── */}
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 bg-muted rounded-lg p-1">
              <button
                onClick={() => setViewMode('detail')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'detail' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
                }>
                <Icon name="Table" size={15} />
                <span>Project Detail</span>
              </button>
              <button
                onClick={() => setViewMode('gantt')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'gantt' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
                }>
                <Icon name="BarChart3" size={15} />
                <span>Gantt Chart</span>
              </button>
              <button
                onClick={() => setViewMode('resource-gantt')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'resource-gantt' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
                }>
                <Icon name="Users" size={15} />
                <span>Resource Gantt</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
                }>
                <Icon name="List" size={15} />
                <span>Card View</span>
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'timeline' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`
                }>
                <Icon name="CalendarRange" size={15} />
                <span>Timeline</span>
              </button>
            </div>
            {/* Live presence badges */}
            {presenceList?.length > 0 &&
              <PresenceBadges
                presenceList={presenceList}
                currentUserId={userProfile?.id}
                label={viewMode === 'gantt' || viewMode === 'resource-gantt' ? 'on Gantt' : 'viewing'}
              />
            }
            </div>

            <Button
              onClick={() => {
                setEditingDeliverable(null);
                setDefaultRowType('deliverable');
                setDefaultParentId('');
                setDefaultPhaseId('');
                setIsAddModalOpen(true);
              }}
              iconName="Plus"
              iconPosition="left">
              Add Item
            </Button>
            <Button
              variant="outline"
              onClick={() => setAdjustDatesModalOpen(true)}
              iconName="CalendarClock"
              iconPosition="left">
              Adjust Dates
            </Button>
            <Button
              variant="outline"
              onClick={() => setExportModalOpen(true)}
              iconName="Download"
              iconPosition="left">
              Export
            </Button>
          </div>

          {/* Error State */}
          {error &&
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          }

          {/* Loading State */}
          {loading &&
          <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          }

          {/* Content */}
          {!loading &&
          <>
              {viewMode === 'list' ?
            <DeliverableListView
              deliverables={deliverables}
              resources={resources}
              phases={phases}
              onEdit={(deliverable) => {
                setEditingDeliverable(deliverable);
                setDefaultRowType(deliverable?.rowType || 'deliverable');
                setDefaultParentId(deliverable?.parentId || '');
                setDefaultPhaseId(deliverable?.phaseId || '');
                setIsAddModalOpen(true);
              }}
              onDelete={handleDeleteDeliverable}
              onUpdate={handleUpdateDeliverable}
              onAddChild={handleAddChild}
              onReorder={handleReorder}
              onAdjustDates={() => setAdjustDatesModalOpen(true)} /> :

            viewMode === 'detail' ?
            <ProjectDetailSpreadsheetView
              deliverables={deliverables}
              resources={resources}
              phases={phases}
              project={project}
              onEdit={(deliverable) => {
                setEditingDeliverable(deliverable);
                setDefaultRowType(deliverable?.rowType || 'deliverable');
                setDefaultParentId(deliverable?.parentId || '');
                setDefaultPhaseId(deliverable?.phaseId || '');
                setIsAddModalOpen(true);
              }}
              onDelete={handleDeleteDeliverable}
              onUpdate={handleUpdateDeliverable}
              onAddChild={handleAddChild}
              onPhaseStatusChange={handlePhaseStatusChange}
              onAddPhase={handleOpenAddPhaseModal}
              onEditPhase={handleOpenEditPhaseFull}
              onReorder={handleReorder} /> :

            viewMode === 'resource-gantt' ?
            <ResourceGanttView
              deliverables={deliverables}
              phases={phases}
              project={project}
              onEdit={(deliverable) => {
                setEditingDeliverable(deliverable);
                setDefaultRowType(deliverable?.rowType || 'deliverable');
                setDefaultParentId(deliverable?.parentId || '');
                setDefaultPhaseId(deliverable?.phaseId || '');
                setIsAddModalOpen(true);
              }}
              onAddChild={handleAddChild}
              onDateChange={handleDateChange}
              onUpdate={handleUpdateDeliverable}
              onAddPhase={handleOpenAddPhaseModal}
              onEditPhase={handleOpenEditPhaseFull}
              onReorder={handleReorder}
              onAdjustDates={() => setAdjustDatesModalOpen(true)} /> :

            viewMode === 'timeline' ?
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                <Icon name="CalendarRange" size={16} className="text-accent" />
                <h3 className="text-sm font-semibold text-foreground">Project Timeline</h3>
                <span className="text-xs text-muted-foreground ml-1">Next 30–90 days</span>
              </div>
              <ProjectTimeline
                deliverables={deliverables}
                projectAssignments={projectAssignments}
                resources={resources}
                phases={phases}
                project={project}
                onReorder={handleReorder}
                userId={userProfile?.id}
                onAdjustDates={() => setAdjustDatesModalOpen(true)} />

            </div> :

            <GanttChartView
              deliverables={deliverables}
              phases={phases}
              project={project}
              onDateChange={handleDateChange}
              onEdit={(deliverable) => {
                setEditingDeliverable(deliverable);
                setDefaultRowType(deliverable?.rowType || 'deliverable');
                setDefaultParentId(deliverable?.parentId || '');
                setDefaultPhaseId(deliverable?.phaseId || '');
                setIsAddModalOpen(true);
              }}
              onUpdate={handleUpdateDeliverable}
              onAddPhase={handleOpenAddPhaseModal}
              onEditPhase={handleOpenEditPhaseFull}
              onReorder={handleReorder}
              userId={userProfile?.id}
              onAdjustDates={() => setAdjustDatesModalOpen(true)}
              onAddItem={async (itemData) => {
                await handleAddDeliverable({ ...itemData, projectId });
              }} />

            }
            </>
          }
        </div>

        {/* Add/Edit Deliverable Modal */}
        <AddDeliverableModal
          isOpen={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingDeliverable(null);
            setDefaultRowType('deliverable');
            setDefaultParentId('');
            setDefaultPhaseId('');
          }}
          onSave={editingDeliverable ? handleUpdateDeliverable : handleAddDeliverable}
          editingDeliverable={editingDeliverable}
          availableResources={resources}
          existingDeliverables={deliverables}
          defaultRowType={defaultRowType}
          defaultParentId={defaultParentId}
          defaultPhaseId={defaultPhaseId}
          project={project}
          phases={phases} />

        {/* Add/Edit Phase Modal */}
        <EditPhaseModal
          isOpen={phaseModalOpen}
          mode={phaseModalMode}
          phase={phaseModalTarget}
          projectId={projectId}
          phases={phases}
          deliverables={deliverables}
          onSave={handlePhaseModalSave}
          onDelete={handlePhaseModalDelete}
          onClose={() => {setPhaseModalOpen(false);setPhaseModalTarget(null);}}
          saving={savingEditPhase} />


        <AddPhasesModal
          isOpen={addPhasesModalOpen}
          onClose={() => setAddPhasesModalOpen(false)}
          projectId={projectId}
          existingPhases={phases}
          onPhasesAdded={() => {loadPhases();}} />


        {/* Section Detail Modal */}
        <SectionDetailModal
          isOpen={sectionDetailOpen}
          onClose={() => {setSectionDetailOpen(false);setSectionDetailType(null);}}
          sectionType={sectionDetailType}
          data={getSectionDetailData()}
          projectName={project?.projectName}
          projectImageService={projectImageService} />

        {/* Export Modal */}
        <ExportModal
          isOpen={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          project={project}
          phases={phases}
          deliverables={deliverables} />

        {/* Adjust Dates Modal */}
        <AdjustDatesModal
          isOpen={adjustDatesModalOpen}
          onClose={() => setAdjustDatesModalOpen(false)}
          onApply={handleAdjustDates}
          saving={adjustDatesSaving}
          deliverables={deliverables} />

      </div>
    </MainLayout>);

};

export default ProjectDetailManagement;